import { Badge, Button, Checkbox, Divider, Empty, Flex, List, Space, Tag, Typography } from 'antd';
import { FC, memo } from 'react';
import { ArrowRepeat, ExclamationDiamondFill, ExclamationTriangleFill, TriangleFill } from 'react-bootstrap-icons';

import { AppModal } from '@/common/components';
import { AppModalControlProps } from '@/common/interfaces';

import { RedirectConflicts, getRedirectSourceLabel, getRedirectTargetLabel } from '@/modules/Sites';

interface Props {
  controls: AppModalControlProps;
  conflicts: RedirectConflicts;
  totalCount: number;
  selectedIds: string[];
  isDeleting: boolean;
  onToggleId: (id: string) => void;
  onDeleteSelected: () => void;
  onDeleteAllRedundant: () => void;
}

const renderLabel = (redirect: RedirectConflicts['redundant'][number]['redirect']): string =>
  `${getRedirectSourceLabel(redirect)} → ${getRedirectTargetLabel(redirect)}`;

export const RedirectsConflictsView: FC<Props> = memo(({
  controls,
  conflicts,
  totalCount,
  selectedIds,
  isDeleting,
  onToggleId,
  onDeleteSelected,
  onDeleteAllRedundant
}) => {
  const hasConflicts = totalCount > 0;

  return (
    <>
      <Badge count={ totalCount } size="small" offset={ [-2, 2] }>
        <Button
          icon={ <ExclamationTriangleFill color={ hasConflicts ? '#faad14' : undefined }/> }
          danger={ hasConflicts }
          onClick={ controls.open }
        >
          Проблемні редіректи
        </Button>
      </Badge>

      <AppModal
        width={ 640 }
        title={
          <Flex align="center" gap={ 8 }>
            <TriangleFill color="#ff4d4f" style={{ transform: 'rotate(180deg)' }}/>
            Проблемні редіректи
          </Flex>
        }
        controls={ controls }
        footer={
          <Flex justify="space-between" align="center">
            <Button
              danger
              disabled={ !conflicts.redundant.length || isDeleting }
              loading={ isDeleting }
              onClick={ onDeleteAllRedundant }
            >
              Видалити всі надлишкові
            </Button>

            <Button
              type="primary"
              danger
              disabled={ !selectedIds.length || isDeleting }
              loading={ isDeleting }
              onClick={ onDeleteSelected }
            >
              Видалити вибрані ({ selectedIds.length })
            </Button>
          </Flex>
        }
      >
        { !hasConflicts ? (
          <Empty description="Проблемних редіректів не знайдено"/>
        ) : (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Надлишкові правила не мають ефекту, бо їх перекривають правила з вищим пріоритетом, або вони ведуть самі
              на себе. Їх можна безпечно видалити. Циклічні правила утворюють нескінченний ланцюжок редіректів.
            </Typography.Paragraph>

            { conflicts.redundant.length > 0 && (
              <div>
                <Divider orientation="left" style={{ margin: '0 0 8px' }}>
                  <Flex align="center" gap={ 6 }>
                    <TriangleFill color="#ff4d4f" style={{ transform: 'rotate(180deg)' }}/>
                    Надлишкові ({ conflicts.redundant.length })
                  </Flex>
                </Divider>

                <List
                  size="small"
                  dataSource={ conflicts.redundant }
                  renderItem={ ({ redirect, overriddenById }) => (
                    <List.Item>
                      <Flex align="center" gap={ 8 } style={{ width: '100%' }}>
                        <Checkbox
                          checked={ selectedIds.includes(redirect.id) }
                          disabled={ isDeleting }
                          onChange={ () => onToggleId(redirect.id) }
                        />
                        <Typography.Text style={{ flex: 1 }}>{ renderLabel(redirect) }</Typography.Text>
                        <Tag color={ overriddenById ? 'orange' : 'default' }>
                          { overriddenById ? 'Перекрито іншим правилом' : 'Веде саме на себе' }
                        </Tag>
                      </Flex>
                    </List.Item>
                  ) }
                />
              </div>
            ) }

            { conflicts.cyclic.length > 0 && (
              <div>
                <Divider orientation="left" style={{ margin: '0 0 8px' }}>
                  <Flex align="center" gap={ 6 }>
                    <ArrowRepeat color="#ff4d4f"/>
                    Циклічні ({ conflicts.cyclic.length })
                  </Flex>
                </Divider>

                <List
                  size="small"
                  dataSource={ conflicts.cyclic }
                  renderItem={ ({ redirect }) => (
                    <List.Item>
                      <Flex align="center" gap={ 8 } style={{ width: '100%' }}>
                        <Checkbox
                          checked={ selectedIds.includes(redirect.id) }
                          disabled={ isDeleting }
                          onChange={ () => onToggleId(redirect.id) }
                        />
                        <Typography.Text style={{ flex: 1 }}>{ renderLabel(redirect) }</Typography.Text>
                        <Tag color="red" icon={ <ExclamationDiamondFill/> }>Цикл</Tag>
                      </Flex>
                    </List.Item>
                  ) }
                />
              </div>
            ) }
          </Space>
        ) }
      </AppModal>
    </>
  );
});
